import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import Spinners from "components/Common/Spinner";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import { useReasonContext } from "libs/context/ReasonContext";
import ReasonAddEditModal from "pages/AllCommonModal/ReasonAddEditModal/reason-add-edit-modal";
import { submitFilterData } from "model/ReasonModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const Reason = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchReasonContextData, reasonContextData } = useReasonContext() || {};
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("Reasons");
    const initialFilterFetchedRef = useRef(false);
    const [reasonAddEditModalOpen, setReasonAddEditModalOpen] = useState(false);
    const [modal1, setModal1] = useState(false);
    const toggleViewModal = () => setModal1(!modal1);
    const [searchQuery, setSearchQuery] = useState("");
    const [reasonData, setReasonData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemId, setItemId] = useState(null); // ✅ important
    const initialState = {
        title: "",
        candidateDeactive: false,
        forTools: false,
        status: true,
    };

    const [formData, setFormData] = useState(initialState);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });


    const filteredData = useMemo(() => reasonData, [reasonData]);

    const reasonActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.MasterReasonsAccess),
            canEdit: Boolean(permissionData?.MasterReasonsAccess),
        }),
        [permissionData]
    );

    const hasAnyReasonRowAction = useMemo(
        () => reasonActionPermissions.canEdit,
        [reasonActionPermissions]
    );

    // ✅ FIXED: set itemId FIRST, then open modal
    const handleEdit = (row) => {
        setItemId(row?.id);
        setReasonAddEditModalOpen(true);
    };

    useEffect(() => {
        fetchReasonContextData();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getReasonList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    useEffect(() => {
        const valueReModified = reasonContextData?.table?.columns?.map(col => ({
            Header: col?.title,
            accessor: pascalToCamel(col?.field),
        }));
        setTableMetadata(valueReModified)
    }, [reasonContextData]);

    const getReasonList = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = {
            ...requestPayload,
            ...payloadOverrides,
        };
        const response = await submitFilterData(payload);
        const responseData = response?.data || {};
        const listData =
            responseData?.records ?? (Array.isArray(responseData) ? responseData : []);
        const valueReModified = (Array.isArray(listData) ? listData : []).map((item) => ({
            ...item,
        }));

        if (response) {
            setReasonData(valueReModified);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || valueReModified.length,
            });
            setRequestPayload(payload);
            setLoading(false);
        } else {
            setReasonData([]);
            setLoading(false);
        }
    };

    const handleFilterSubmit = async (data) => {
        await getReasonList({
            ...data,
            search: requestPayload?.search || "",
            pageNumber: 1,
        });
        setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value) => {
        const trimmedValue = value?.trim() || "";
        await getReasonList({
            ...requestPayload,
            pageNumber: 1,
            search: trimmedValue,
        });
    };

    const handlePageChange = async (page) => {
        await getReasonList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getReasonList({ pageNumber: 1, pageSize: rows });
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
                    {/* Table */}
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Reason</h5>
                                {reasonActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setItemId(null);
                                                setReasonAddEditModalOpen(true);
                                                setFormData(initialState);
                                            }}
                                        >
                                            + Create
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setSearchQuery(nextValue);
                                                if (!nextValue.trim()) {
                                                    handleSearchSubmit("");
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />

                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {/* <hr /> */}
                            {/* <div className="mb-4 h4 card-title">Job Category</div> */}
                            {!loading && tableMetaData?.length ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyReasonRowAction}
                                    onEdit={
                                        reasonActionPermissions.canEdit ? handleEdit : undefined
                                    }
                                    isEditAccess={reasonActionPermissions.canEdit}
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"Reasons"}
                                />
                            ) : 
                            // <Spinners setLoading={setLoading} />
                            <Loaders />
                            }
                        </CardBody>
                    </Card>

                    {/* Add/Edit Modal */}
                    <ReasonAddEditModal
                        setReasonAddEditModalOpen={setReasonAddEditModalOpen}
                        reasonAddEditModalOpen={reasonAddEditModalOpen}
                        getReasonList={getReasonList}
                        itemId={itemId}
                        setReasonData={setReasonData}
                        setFormData={setFormData}
                        formData={formData}
                        totalData={reasonData}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

Reason.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(Reason);
